/**
 * DANE EDUKACYJNE — KONTYNENT EUROPA (biom „Świat Flag").
 *
 * Każdy wpis odpowiada jednemu stworkowi-fladze z `pets.config.ts`
 * (`pet-flags-<kraj>`). Zawiera: fakty (stolica + kontynent), zwroty w języku
 * kraju (powitanie + dziękuję), krótką ciekawostkę i zabytek.
 *
 * Dodanie kolejnego kraju = jeden wpis tutaj + odpowiedni `PetDef` w pets.config.ts
 * + assety (flaga + zabytek). Bez zmian w mechanice.
 */

import type { EduEntry } from '@/domain/types/edu';
import type { ID, LocalizedString } from '@/domain/types/common';

const CONTINENT_EUROPE: LocalizedString = { pl: 'Europa', en: 'Europe' };
const LABEL_CAPITAL: LocalizedString = { pl: 'Stolica', en: 'Capital' };
const LABEL_CONTINENT: LocalizedString = { pl: 'Kontynent', en: 'Continent' };

interface CountryInput {
  petId: ID;
  capital: LocalizedString;
  /** Powitanie w języku kraju + znaczenie PL. */
  hello: { native: string; pl: string };
  /** Podziękowanie w języku kraju + znaczenie PL. */
  thanks: { native: string; pl: string };
  funFact: LocalizedString;
  landmark: { imageKey: string; caption: LocalizedString };
}

/** Buduje `EduEntry` z lekkiego opisu kraju (mniej powtórzeń przy wielu krajach). */
function country(input: CountryInput): EduEntry {
  return {
    facts: [
      { label: LABEL_CAPITAL, value: input.capital },
      { label: LABEL_CONTINENT, value: CONTINENT_EUROPE },
    ],
    phrases: [
      { labelPl: 'Powitanie', native: input.hello.native, pl: input.hello.pl, iconKey: 'ui_btn_comms' },
      { labelPl: 'Dziękuję', native: input.thanks.native, pl: input.thanks.pl, iconKey: 'ui_comm_thanks' },
    ],
    funFact: input.funFact,
    landmark: input.landmark,
  };
}

const COUNTRIES: CountryInput[] = [
  {
    petId: 'pet-flags-poland',
    capital: { pl: 'Warszawa', en: 'Warsaw' },
    hello: { native: 'Dzień dobry', pl: 'dzień dobry' },
    thanks: { native: 'Dziękuję', pl: 'dziękuję' },
    funFact: { pl: 'Polska leży nad Morzem Bałtyckim, a jej godłem jest biały orzeł.', en: 'Poland lies by the Baltic Sea and its emblem is a white eagle.' },
    landmark: { imageKey: 'landmark_poland', caption: { pl: 'Zamek Królewski na Wawelu', en: 'Wawel Royal Castle' } },
  },
  {
    petId: 'pet-flags-germany',
    capital: { pl: 'Berlin', en: 'Berlin' },
    hello: { native: 'Guten Tag', pl: 'dzień dobry' },
    thanks: { native: 'Danke', pl: 'dziękuję' },
    funFact: { pl: 'Niemcy słyną z samochodów i smacznych precli.', en: 'Germany is famous for cars and tasty pretzels.' },
    landmark: { imageKey: 'landmark_germany', caption: { pl: 'Brama Brandenburska', en: 'Brandenburg Gate' } },
  },
  {
    petId: 'pet-flags-czechia',
    capital: { pl: 'Praga', en: 'Prague' },
    hello: { native: 'Dobrý den', pl: 'dzień dobry' },
    thanks: { native: 'Děkuji', pl: 'dziękuję' },
    funFact: { pl: 'W Pradze stoi słynny most Karola z wieloma rzeźbami.', en: 'Prague has the famous Charles Bridge with many statues.' },
    landmark: { imageKey: 'landmark_czechia', caption: { pl: 'Most Karola w Pradze', en: 'Charles Bridge, Prague' } },
  },
  {
    petId: 'pet-flags-slovakia',
    capital: { pl: 'Bratysława', en: 'Bratislava' },
    hello: { native: 'Dobrý deň', pl: 'dzień dobry' },
    thanks: { native: 'Ďakujem', pl: 'dziękuję' },
    funFact: { pl: 'Słowację pokrywają wysokie góry — Tatry.', en: 'Slovakia is covered by tall mountains — the Tatras.' },
    landmark: { imageKey: 'landmark_slovakia', caption: { pl: 'Zamek w Bratysławie', en: 'Bratislava Castle' } },
  },
  {
    petId: 'pet-flags-france',
    capital: { pl: 'Paryż', en: 'Paris' },
    hello: { native: 'Bonjour', pl: 'dzień dobry' },
    thanks: { native: 'Merci', pl: 'dziękuję' },
    funFact: { pl: 'We Francji, w Paryżu, stoi słynna wieża Eiffla.', en: 'France, in Paris, has the famous Eiffel Tower.' },
    landmark: { imageKey: 'landmark_france', caption: { pl: 'Wieża Eiffla', en: 'Eiffel Tower' } },
  },
  {
    petId: 'pet-flags-italy',
    capital: { pl: 'Rzym', en: 'Rome' },
    hello: { native: 'Buongiorno', pl: 'dzień dobry' },
    thanks: { native: 'Grazie', pl: 'dziękuję' },
    funFact: { pl: 'Włochy to ojczyzna pizzy i makaronu.', en: 'Italy is the homeland of pizza and pasta.' },
    landmark: { imageKey: 'landmark_italy', caption: { pl: 'Koloseum w Rzymie', en: 'Colosseum, Rome' } },
  },
  {
    petId: 'pet-flags-spain',
    capital: { pl: 'Madryt', en: 'Madrid' },
    hello: { native: 'Buenos días', pl: 'dzień dobry' },
    thanks: { native: 'Gracias', pl: 'dziękuję' },
    funFact: { pl: 'W Hiszpanii tańczy się ognisty taniec flamenco.', en: 'In Spain people dance the fiery flamenco.' },
    landmark: { imageKey: 'landmark_spain', caption: { pl: 'Sagrada Família w Barcelonie', en: 'Sagrada Família, Barcelona' } },
  },
  {
    petId: 'pet-flags-uk',
    capital: { pl: 'Londyn', en: 'London' },
    hello: { native: 'Good morning', pl: 'dzień dobry' },
    thanks: { native: 'Thank you', pl: 'dziękuję' },
    funFact: { pl: 'Po Londynie jeżdżą czerwone, piętrowe autobusy.', en: 'Red double-decker buses ride around London.' },
    landmark: { imageKey: 'landmark_uk', caption: { pl: 'Big Ben w Londynie', en: 'Big Ben, London' } },
  },
  {
    petId: 'pet-flags-ukraine',
    capital: { pl: 'Kijów', en: 'Kyiv' },
    hello: { native: 'Dobryj deń', pl: 'dzień dobry' },
    thanks: { native: 'Diakuju', pl: 'dziękuję' },
    funFact: { pl: 'Ukraina to największy kraj leżący w całości w Europie.', en: 'Ukraine is the largest country lying entirely in Europe.' },
    landmark: { imageKey: 'landmark_ukraine', caption: { pl: 'Sobór Mądrości Bożej w Kijowie', en: 'Saint Sophia Cathedral, Kyiv' } },
  },
  {
    petId: 'pet-flags-lithuania',
    capital: { pl: 'Wilno', en: 'Vilnius' },
    hello: { native: 'Laba diena', pl: 'dzień dobry' },
    thanks: { native: 'Ačiū', pl: 'dziękuję' },
    funFact: { pl: 'Litwa jest sąsiadem Polski na północnym wschodzie.', en: 'Lithuania is Poland’s neighbour to the north-east.' },
    landmark: { imageKey: 'landmark_lithuania', caption: { pl: 'Wieża Giedymina w Wilnie', en: 'Gediminas’ Tower, Vilnius' } },
  },
  {
    petId: 'pet-flags-sweden',
    capital: { pl: 'Sztokholm', en: 'Stockholm' },
    hello: { native: 'God dag', pl: 'dzień dobry' },
    thanks: { native: 'Tack', pl: 'dziękuję' },
    funFact: { pl: 'Ze Szwecji pochodzą meble ze sklepów IKEA.', en: 'IKEA furniture comes from Sweden.' },
    landmark: { imageKey: 'landmark_sweden', caption: { pl: 'Ratusz w Sztokholmie', en: 'Stockholm City Hall' } },
  },
  {
    petId: 'pet-flags-norway',
    capital: { pl: 'Oslo', en: 'Oslo' },
    hello: { native: 'God dag', pl: 'dzień dobry' },
    thanks: { native: 'Takk', pl: 'dziękuję' },
    funFact: { pl: 'W Norwegii są piękne fiordy — długie zatoki między górami.', en: 'Norway has beautiful fjords — long bays between mountains.' },
    landmark: { imageKey: 'landmark_norway', caption: { pl: 'Fiordy norweskie', en: 'Norwegian fjords' } },
  },
  {
    petId: 'pet-flags-greece',
    capital: { pl: 'Ateny', en: 'Athens' },
    hello: { native: 'Kalimera', pl: 'dzień dobry' },
    thanks: { native: 'Efcharistó', pl: 'dziękuję' },
    funFact: { pl: 'W Grecji narodziły się igrzyska olimpijskie.', en: 'The Olympic Games were born in Greece.' },
    landmark: { imageKey: 'landmark_greece', caption: { pl: 'Akropol w Atenach', en: 'Acropolis, Athens' } },
  },
  {
    petId: 'pet-flags-iceland',
    capital: { pl: 'Reykjavik', en: 'Reykjavík' },
    hello: { native: 'Góðan dag', pl: 'dzień dobry' },
    thanks: { native: 'Takk', pl: 'dziękuję' },
    funFact: { pl: 'Na Islandii są wulkany i tryskające gorącą wodą gejzery.', en: 'Iceland has volcanoes and geysers that shoot hot water.' },
    landmark: { imageKey: 'landmark_iceland', caption: { pl: 'Gejzer na Islandii', en: 'A geyser in Iceland' } },
  },
  {
    petId: 'pet-flags-portugal',
    capital: { pl: 'Lizbona', en: 'Lisbon' },
    hello: { native: 'Bom dia', pl: 'dzień dobry' },
    thanks: { native: 'Obrigado', pl: 'dziękuję' },
    funFact: { pl: 'Z Portugalii wypływali słynni żeglarze i odkrywcy.', en: 'Famous sailors and explorers set off from Portugal.' },
    landmark: { imageKey: 'landmark_portugal', caption: { pl: 'Wieża Belém w Lizbonie', en: 'Belém Tower, Lisbon' } },
  },
];

/** Wpisy edukacyjne kontynentu Europa, kluczowane po `petId`. */
export const FLAGS_EUROPE_EDU: Record<ID, EduEntry> = Object.fromEntries(
  COUNTRIES.map((c) => [c.petId, country(c)]),
);
